// ProfileModal — v32: JEDEN zjednotený profil. Popup po kliknutí na profilovú fotku ukazuje
// CELÝ verejný profil (ProfileBody — rovnaké telo ako stránka /u/:id).
// v32.21 — akcie (Šťuchnúť tlačidlo + „⋮" so správou a výzvou) sú ZABUDOVANÉ priamo sem a
// riadia sa údajmi profilu (rel/is_me). Konkrétne handlery (chat, poke, create, pridanie
// priateľa) dodáva appka RAZ cez `actions` (globálny ProfileModalProvider) — žiadne per-caller
// kópie. Fetchuje /api/users/:id/profile.
import { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { BuzerIcon } from "../../BuzerIcons";
import { Icon } from "../../icons";
import { st } from "../../styles.js";
import { ProfileBody, profileTier, ProfileNameBadge } from "./ProfileBody";
import { tierCardStyle, liteCardStyle } from "./Avatar";
import { ActionMenu, IconButton, Sheet } from "./ds";

export function ProfileModal({ uid, onClose, actions, highlightReaction = null }) {
  const [p, setP] = useState(null);
  const [err, setErr] = useState("");
  const [menu, setMenu] = useState(false);
  const load = useCallback(() => api("/api/users/" + uid + "/profile").then(setP).catch((e) => setErr(e.message)), [uid]);
  useEffect(() => { load(); }, [load]);
  async function react(emoji) {
    try { await api("/api/users/" + uid + "/status/react", { method: "POST", body: { emoji } }); load(); }
    catch (e) { /* */ }
  }
  const a = actions || {};
  const isFriend = !!(p && !p.is_me && p.rel === "friend");
  const canAdd = !!(p && !p.is_me && p.rel === "none" && a.addFriend);
  const showPoke = isFriend && !!a.poke;
  // „⋮" menu — správa + spoločná výzva/súťaž + šťuchnutie (len pre priateľov). v34 B8 — Šťuchnúť za Výzvou.
  const menuItems = !isFriend ? [] : [
    a.message && { icon: "chat", label: "Napísať správu", onClick: () => a.message(p.id, p.name) },
    a.create && { icon: "flag", label: "Výzva", desc: "Spoločná výzva alebo súťaž", onClick: () => a.create(p.id, p.name, "challenge") },
    a.poke && { icon: "buzerak", label: "Šťuchnúť", onClick: () => a.poke(p.id, p.name) },
    // T6 — deštruktívna akcia dole (oddelená deličom): odobrať z priateľov s potvrdením.
    // Friendship id (p.fid) dodáva backend v payloade profilu (rel==="friend").
    a.removeFriend && p.fid && { icon: "delete", label: "Odobrať z priateľov", danger: true, dividerBefore: true, onClick: () => a.removeFriend(p.fid, p.name) },
  ].filter(Boolean);
  // v34 B1 — profil = rovnaký shell ako detail návyku (Sheet center: centrovaný dialóg s neviditeľnými
  //          okrajmi, full-width). Hlavička Sheetu nesie meno + Community/Solo badge (B7) a akcie (B4).
  // v33h/v34 — CELÝ Sheet (hlavička aj telo) je prefarbený podľa tieru cez Avatar.tierCardStyle
  //          (rovnako ako predtým Modal): tokeny prefarbia titul, ×, ghost tlačidlá aj obsah na
  //          biele-na-farbe. Lite/bez levelu → null (neutrálny Sheet, pôvodné správanie).
  // Item 2 — Lite profil (tier −1) dostane sivú „grey tier" kartu (rovnaké elementy ako farebný,
  //          len tlmený sivý povrch); ostatní podľa levelu (tierCardStyle), non-lite bez zmeny.
  const cardStyle = p && p.lite ? liteCardStyle() : tierCardStyle(profileTier(p));
  return (
    <Sheet center onClose={onClose} cardStyle={cardStyle}
      title={
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8, minWidth: 0, flexWrap: "wrap" }}>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p ? p.name : "Profil"}</span>
          {p && <ProfileNameBadge premium={p.premium} />}
        </span>
      }
      headerRight={(showPoke || menuItems.length > 0) && (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          {showPoke && (
            /* v34 B4 — Šťuchnúť má rovnaký ghost/outline štýl ako tri bodky (nie plná brand výplň) */
            <IconButton variant="ghost" size="sm" aria-label="Šťuchnúť" title="Šťuchnúť" onClick={() => a.poke(p.id, p.name)}>
              <BuzerIcon name="buzerak" size={16} color="currentColor" />
            </IconButton>
          )}
          {menuItems.length > 0 && (
            <IconButton variant="ghost" size="sm" aria-label="Akcie" title="Akcie" onClick={() => setMenu(true)}>
              <BuzerIcon name="dots" size={16} color="currentColor" style={{ transform: "rotate(90deg)" }} />
            </IconButton>
          )}
        </span>
      )}>
      {err && <div style={st.err}>{err}</div>}
      {!p ? <div style={{ display: "flex", justifyContent: "center", padding: 24 }}><div className="spin" /></div> : (
        <>
          {/* tier prefarbenie prichádza z celého Sheetu (cardStyle) — netreba lokálny obal */}
          <ProfileBody p={p} onReact={react} hideName highlightReaction={highlightReaction} />
          {/* nie je priateľ → jediná akcia je žiadosť o priateľstvo (viditeľné tlačidlo, nie ⋮) */}
          {canAdd && (
            <button onClick={() => a.addFriend(p.id)} style={{ ...st.primaryBtn, marginTop: 14, width: "100%", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7 }}>
              <Icon name="addfriend" size={17} color="currentColor" /> Pridať priateľa
            </button>
          )}
        </>
      )}
      {menu && <ActionMenu title={p ? p.name : "Akcie"} items={menuItems} onClose={() => setMenu(false)} />}
    </Sheet>
  );
}
